import React, { useContext, useState } from "react";
import { Card, Col, Row } from "react-bootstrap";
import {
    FaLanguage,
    FaShapes,
    FaClone,
    FaCalendarAlt,
    FaClock,
    FaCoins,
} from "react-icons/fa";
import moment from "moment";
import TakeThisPollItem from "./TakeThisPollItem";
import { Context } from "../../index";
import { updatePoolItem } from "../../http/poolAPI";
import noImagePoll from "../../assets/no-image-poll.jpg";
import TripleSpinner from "../../helpers/TripleSpinner";

/**
 * Shared cover with loading spinner + smooth fade-in
 */
const CoverWithSpinner = ({
                              src,
                              fallbackSrc,
                              alt = "",
                              width = "auto",
                              height = 148,
                              fetchPriority = "auto",
                              style = {},
                          }) => {
    const [loaded, setLoaded] = useState(false);
    const [err, setErr] = useState(false);

    const finalSrc = err ? fallbackSrc : src;

    return (
        <div
            style={{
                position: "relative",
                width,
                height,
                borderRadius: 1,
                overflow: "hidden",
                background: "#fff",
                ...style,
            }}
        >
            {!loaded && (
                <div
                    style={{
                        position: "absolute",
                        inset: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                    aria-hidden="true"
                >
                    <TripleSpinner size={24} gapRatio={0.8} />
                </div>
            )}

            <img
                src={finalSrc}
                alt={alt}
                loading="lazy"
                decoding="async"
                fetchpriority={fetchPriority}
                onLoad={() => setLoaded(true)}
                onError={() => {
                    setErr(true);
                    setLoaded(true);
                }}
                style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                    display: "block",
                    opacity: loaded ? 1 : 0,
                    transition: "opacity 500ms ease-in",
                    backgroundColor: "white",
                    paddingTop: 0,
                    paddingBottom: 0,
                    borderRadius: 6,
                }}
            />
        </div>
    );
};

const PollLibraryItem = ({ pollitem }) => {
    const { user } = useContext(Context);
    const poll = pollitem.pool;
    const [takeSurveyVisible, setTakeSurveyVisible] = useState(false);
    const [isYourPoll, setIsYourPoll] = useState(false);
    const isMobile = window.innerWidth < 480; // swap to a media hook if needed

    const activeFlags = Array.from({ length: 8 }).map((_, i) =>
        Boolean(poll[`pool_item${i + 1}_flag`])
    );
    const activeCount = activeFlags.filter(Boolean).length;

    // Build a normalized list of up to 8 images with API URLs
    const allImages = Array.from({ length: 8 }, (_, i) => {
        const img = poll[`pool_item${i + 1}_image`];
        const flag = poll[`pool_item${i + 1}_flag`];
        return flag
            ? {
                image: img ? `${process.env.REACT_APP_API_URL}pools/${img}` : noImagePoll,
                alt: `${poll.pool_type} item ${i + 1}`,
            }
            : null;
    }).filter(Boolean);

    const editPollItemWhenTakeSurvey = async () => {
        try {
            const updatedItem = {
                itemId: pollitem.id,
                respondent_id: user?.user?.id,
                respondentId: user?.user?.id,
                respondent_name: user?.user?.amz_name?.trim() || user?.user?.username,
                respondent_step: "STEP1",
                start_date: Math.floor(Date.now() / 60000),
            };

            await updatePoolItem(updatedItem);
        } catch (error) {
            console.error("Error updating poll item:", error);
            alert(
                "An error occurred while updating the poll item. Please try again later."
            );
        }
    };

    const ModalOpen = async () => {
        setIsYourPoll(poll?.userId === user?.user?.id);
        setTakeSurveyVisible(true);
        await editPollItemWhenTakeSurvey();
    };

    const PollTypeName = ({ bold = false }) => {
        const map = {
            COVER_POLL: "Cover Poll",
            TITLE_POLL: "Title Poll",
            COVER_TITLE_POLL: "Cover&Title Poll",
            DESCRIPTION_POLL: "Description Poll",
            MIXED_POLL: "Mixed Poll",
        };
        const text = map[poll.pool_type] || "Unknown";
        return bold ? <strong>{text}</strong> : <>{text}</>;
    };

    /**
     * Desktop media area renderer (left side)
     */
    const renderDesktopMedia = () => {
        // 1 active => big single image w/ spinner
        if (allImages.length === 1) {
            return (
                <CoverWithSpinner
                    src={allImages[0].image}
                    fallbackSrc={noImagePoll}
                    alt={allImages[0].alt}
                    height={136}
                    width="96%"
                    style={{ maxWidth: 146 }}
                />
            );
        }

        // 2–6 actives => grid of covers, each with its own spinner
        const sliceCount = allImages.length >= 6 ? 6 : allImages.length >= 3 ? 3 : 2;
        const grid = allImages.slice(0, sliceCount);

        // Compute slot height to keep a neat grid
        const slotHeight =
            sliceCount === 2 ? 119 : sliceCount === 3 ? 87 : /* 6 */ 67;

        return (
            <div
                style={{
                    display: 'grid',
                    gap: '4px',
                    width: '100%',
                    height: '100%',
                    alignItems: 'center',
                    justifyItems: 'center',
                    gridTemplateColumns:
                        allImages.length >= 6
                            ? 'repeat(3, 1fr)'
                            : allImages.length >= 3
                                ? 'repeat(3, 1fr)'
                                : 'repeat(2, 1fr)',

                }}
            >
                {grid.map((it, idx) => (
                    <CoverWithSpinner
                        key={idx}
                        src={it.image}
                        fallbackSrc={noImagePoll}
                        alt={it.alt}
                        height={slotHeight}
                        width="100%"
                        style={{ borderRadius: 4 }}
                    />
                ))}
            </div>
        );
    };

    /**
     * Mobile media area renderer (left column)
     * Show the first 2 active items with spinners
     */
    const renderMobileMedia = () => {
        const firstTwo = allImages.slice(0, 2);
        const H = 104; // image height on mobile

        if (firstTwo.length === 0) {
            return (
                <CoverWithSpinner
                    src={noImagePoll}
                    fallbackSrc={noImagePoll}
                    alt="No image"
                    height={H}
                    width={"100%"}
                />
            );
        }

        return (
            <div className="w-full d-flex justify-content-center align-items-center gap-2">
                {firstTwo.map((it, idx) => (
                    <CoverWithSpinner
                        key={idx}
                        src={it.image}
                        fallbackSrc={noImagePoll}
                        alt={it.alt}
                        height={H}
                        width={"46%"}
                        style={{ maxWidth: 80 }}
                    />
                ))}
            </div>
        );
    };

    return (
        <>
            {/* Desktop card */}
            {!isMobile && (
                <Col xs={12} sm={6} md={4} lg={4} className="mb-2 d-flex justify-content-center">
                    <Card
                        style={{
                            borderRadius: "8px",
                            boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
                            transition: "transform 0.2s, box-shadow 0.3s",
                            cursor: "pointer",
                            height: 158,
                            width: 380,
                        }}
                        className="border-secondary border-opacity-75"
                        onClick={ModalOpen}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.boxShadow = "0px 6px 16px rgba(0, 0, 0, 0.2)";
                            e.currentTarget.style.transform = "translateY(-2px)";
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.boxShadow = "0px 4px 12px rgba(0, 0, 0, 0.1)";
                            e.currentTarget.style.transform = "translateY(0)";
                        }}
                    >
                        <Card.Body className="d-flex flex-column p-2">
                            <div className="d-flex justify-content-center gap-2 mb-2">
                                <Col md={6} className="d-flex justify-content-center align-items-center position-relative"
                                     style={{
                                         borderRadius: "4px",
                                         overflow: "hidden",
                                         backgroundColor: "white",
                                         maxHeight: '140px',
                                         paddingLeft: '4px',
                                         paddingRight: '2px',
                                     }}
                                >
                                    {renderDesktopMedia()}
                                </Col>

                                <Col md={6} className="flex-column justify-content-lg-start align-text-top p-0">
                                    <div className="text-muted mt-auto" style={{ fontSize: "0.80rem", lineHeight: "1.2rem" }}>
                                        <p
                                            className="mb-0 text-success"
                                            style={{
                                                display: "-webkit-box",
                                                WebkitBoxOrient: "vertical",
                                                overflow: "hidden",
                                                WebkitLineClamp: 1,
                                                fontSize: "1.0rem",
                                            }}
                                        >
                                            <strong>
                                                <PollTypeName /> #{poll?.id}-{pollitem?.id}
                                            </strong>
                                        </p>
                                        <div>
                                            <FaShapes className="me-1" />
                                            Type:&nbsp;
                                            <PollTypeName bold />
                                        </div>
                                        <div>
                                            <FaClone className="me-1" />Items: <strong>{activeCount} Poll Items</strong>
                                        </div>
                                        <div>
                                            <FaCalendarAlt className="me-1" />Created: <strong>{moment(poll.createdAt).format("DD-MMM-YYYY")}</strong>
                                        </div>
                                        <div>
                                            <FaLanguage className="me-1" />Language: <strong>{poll.language}</strong>
                                        </div>
                                        <div>
                                            <FaClock className="me-1" />Time: <strong>{(pollitem?.time_to_answer || 0) / 60} Hours</strong>
                                        </div>
                                        <div>
                                            <FaCoins className="me-1" />Earnings: <strong className="text-success">{poll?.default_price || 90} Virt</strong>
                                        </div>
                                    </div>
                                </Col>
                            </div>
                        </Card.Body>
                    </Card>

                    <TakeThisPollItem
                        show={takeSurveyVisible}
                        handleClose={() => setTakeSurveyVisible(false)}
                        pollitem={pollitem}
                        isYourPoll={isYourPoll}
                    />
                </Col>
            )}

            {/* Mobile card */}
            {isMobile && (
                <Col xs={12} className="d-flex justify-content-center">
                    <Card
                        className="mt-2 mb-2 p-2 mx-auto"
                        style={{
                            borderRadius: 8,
                            boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                            width: "100%",
                            cursor: "pointer",
                            maxWidth: 380,
                        }}
                        onClick={ModalOpen}
                    >
                        <Row className="g-2 align-items-center mb-1 text-muted">
                            <Col xs={6} className="d-flex justify-content-center align-items-center gap-2">
                                {renderMobileMedia()}
                            </Col>
                            <Col xs={6}>
                                <p
                                    className="mb-1 text-success fw-bold small text-truncate mt-1"
                                    style={{
                                        lineHeight: "1.3em",
                                        display: "-webkit-box",
                                        WebkitBoxOrient: "vertical",
                                        overflow: "hidden",
                                        WebkitLineClamp: 1,
                                    }}
                                >
                  <span>
                    <PollTypeName /> #{poll?.id}-{pollitem?.id}
                  </span>
                                </p>
                                <p
                                    className="mb-1 small"
                                    style={{
                                        lineHeight: "0.9em",
                                        display: "-webkit-box",
                                        WebkitBoxOrient: "vertical",
                                        overflow: "hidden",
                                        WebkitLineClamp: 1,
                                    }}
                                >
                                    <FaShapes className="me-1" />Type:&nbsp;
                                    <PollTypeName bold />
                                </p>

                                <p
                                    className="mb-1 small"
                                    style={{
                                        lineHeight: "0.9em",
                                        display: "-webkit-box",
                                        WebkitBoxOrient: "vertical",
                                        overflow: "hidden",
                                        WebkitLineClamp: 1,
                                    }}
                                >
                                    <FaClone className="me-1" />Items: <strong>{activeCount} Poll Items</strong>
                                </p>
                                <p
                                    className="mb-1 small"
                                    style={{
                                        lineHeight: "0.9em",
                                        display: "-webkit-box",
                                        WebkitBoxOrient: "vertical",
                                        overflow: "hidden",
                                        WebkitLineClamp: 1,
                                    }}
                                >
                                    <FaLanguage className="me-1" />Language: <strong>{poll.language}</strong>
                                </p>
                                <p
                                    className="mb-1 small"
                                    style={{
                                        lineHeight: "0.9em",
                                        display: "-webkit-box",
                                        WebkitBoxOrient: "vertical",
                                        overflow: "hidden",
                                        WebkitLineClamp: 1,
                                    }}
                                >
                                    <FaClock className="me-1" />ToDo Time: <strong>{(pollitem?.time_to_answer || 0) / 60} Hours</strong>
                                </p>
                                <p
                                    className="mb-0 small"
                                    style={{
                                        lineHeight: "0.9em",
                                        display: "-webkit-box",
                                        WebkitBoxOrient: "vertical",
                                        overflow: "hidden",
                                        WebkitLineClamp: 1,
                                    }}
                                >
                                    <FaCoins className="me-1" />Earnings: <strong className="text-success">{poll?.default_price || 90} Virt</strong>
                                </p>
                            </Col>
                        </Row>

                        <TakeThisPollItem
                            show={takeSurveyVisible}
                            handleClose={() => setTakeSurveyVisible(false)}
                            pollitem={pollitem}
                            isYourPoll={isYourPoll}
                        />
                    </Card>
                </Col>
            )}
        </>
    );
};

export default PollLibraryItem;
